import React from "react";
import { StaticQuery, graphql } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import Button from "./atoms/Button";
import CityCallButton from "./city-call-button";
import RoofingCalculator from "./sidebars/roof-calc";
import cityPageContent from "../data/city-page-content";

/**
 * `section` picks which block renders. The design orders these as
 * emergency -> permits -> "Get A New Roof In 3 Steps" -> calculator -> costs,
 * so each has to be placeable independently. "all" keeps the original order.
 */
const JacksonEmergencyAndCosts = ({ city = "Jackson", section = "all" }) => (
  <StaticQuery
    query={graphql`
      query {
        emergencyImage: file(relativePath: { eq: "roofing_bg.jpg" }) {
          childImageSharp {
            gatsbyImageData(width: 800)
          }
        }
        costsImage: file(relativePath: { eq: "roofing_bg.jpg" }) {
          childImageSharp {
            gatsbyImageData(width: 800)
          }
        }
        permitImage: file(relativePath: { eq: "roofing_bg.jpg" }) {
          childImageSharp {
            gatsbyImageData(width: 800)
          }
        }
      }
    `}
    render={(data) => {
      const emergencyImg = getImage(data.emergencyImage.childImageSharp);
      const costsImg = getImage(data.costsImage.childImageSharp);
      const permitImg = getImage(data.permitImage.childImageSharp);
      const show = (name) => section === "all" || section === name;
      const { permitsBody } = cityPageContent.forCity(city);

      return (
        <section className="py-12 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12 md:space-y-20">
            {show("emergency") ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-display font-extrabold text-neutral-900 mb-6 leading-tight">
                  Emergency Roofing In Jackson, MI
                </h2>
                <div className="space-y-4 text-neutral-700 leading-relaxed">
                  <p>
                    We've gotten calls at 3 AM from panicked homeowners watching water
                    drip into their living room. We've shown up on Christmas morning to tarp a
                    storm damaged roof. Roofing emergencies don't wait for convenient
                    moments.
                  </p>
                  <p>
                    That's why our phone is always on. When you're standing in your hallway
                    with a bucket, watching that dark spot on your ceiling grow bigger, the last
                    thing you want to hear is "we'll get back to you Monday morning."
                  </p>
                  <p>
                    When you call us, we answer. And we show up, usually within the hour. Our
                    first job isn't to sell you a whole new roof. It's to stop the damage. We'll
                    patch that leak, get tarps where they need to be, and make sure the
                    problem doesn't spread to your insulation, drywall, or belongings.
                  </p>
                  <p>
                    Once everything's stabilized, we'll walk you through what comes next and
                    discuss your options for complete restoration.
                  </p>
                  <p>
                    We've lived through enough Michigan winters and spring storms to know
                    what Jackson roofs go through. After years of responding to these
                    emergencies, we understand that homeowners need more than just a
                    roofer. They need someone who shows up quickly and takes their situation
                    seriously.
                  </p>
                </div>
              </div>

              <div className="rounded-xl overflow-hidden bg-neutral-200 min-h-[260px] md:min-h-[360px] lg:min-h-[460px]">
                {emergencyImg ? (
                  <GatsbyImage
                    image={emergencyImg}
                    alt="Emergency roofing services in Jackson, MI"
                    className="w-full h-full"
                  />
                ) : null}
              </div>
            </div>
            ) : null}

            {show("calculator") ? (
            <div className="rounded-2xl overflow-hidden relative px-4 sm:px-8 py-12 md:py-16">
              <RoofingCalculator />
            </div>
            ) : null}

            {show("costs") ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="rounded-xl overflow-hidden bg-neutral-200 min-h-[260px] md:min-h-[360px] lg:min-h-[460px] order-2 lg:order-1">
                {costsImg ? (
                  <GatsbyImage
                    image={costsImg}
                    alt="Roofing costs in Jackson, MI"
                    className="w-full h-full"
                  />
                ) : null}
              </div>

              <div className="order-1 lg:order-2">
                <h2 className="text-3xl md:text-4xl font-display font-extrabold text-neutral-900 mb-6 leading-tight">
                  Roofing Costs In Jackson, MI
                </h2>
                <div className="space-y-4 text-neutral-700 leading-relaxed">
                  <p>
                    Roofing costs in Jackson, MI vary based on the materials you choose, the
                    size of your roof, and how complex the job is. For a typical home between
                    1,700 and 2,000 square feet, most homeowners pay between{" "}
                    <strong>$8,000 and $13,500</strong> for a standard asphalt shingle
                    replacement. That breaks down to about{" "}
                    <strong>$4.25 to $7.00</strong> per square foot.
                  </p>
                  <p>
                    Keep in mind that your total cost will go up if you need to remove old
                    shingles, repair the roof deck, or upgrade ventilation. These are common
                    additions that affect the final price.
                  </p>
                  <p>
                    If you're looking at premium materials, here's what to expect. Architectural
                    shingles typically cost $6.50 to $9.50+ per square foot. Metal roofing runs
                    higher at $9.50 to $17 per square foot, depending on the style. Flat or low-
                    slope roofs usually fall in the $4.75 to $8.50 per square foot range.
                  </p>
                  <p>
                    For smaller repair jobs in Jackson County, you're looking at anywhere from
                    $225 to $1,400. The exact price depends on where the leak is and how easy it
                    is to access.
                  </p>
                  <p>
                    Other things can affect your final cost too. Permit fees, dumpster rental,
                    steep roof pitches, skylights, and the time of year you schedule the work all
                    play a role in pricing.
                  </p>
                </div>
              </div>
            </div>
            ) : null}

            {show("costs") ? (
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Button color="red" size="big" to="/contact/">
                Schedule a Free Consultation
              </Button>
              <CityCallButton city={city} />
            </div>
            ) : null}

            {show("permits") ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-display font-extrabold text-neutral-900 mb-6 leading-tight">
                  Do You Need Permit For Roofing In Jackson, MI?
                </h2>
                <div className="space-y-4 text-neutral-700 leading-relaxed">
                  {permitsBody ? (
                    <p>{permitsBody}</p>
                  ) : (
                    <>
                  <p>
                    Full replacements, major repairs, and structural work all need
                    proper permitting to meet local building codes and safety
                    standards. It's not just bureaucracy. Permits ensure your roof is
                    built correctly and safely.
                  </p>
                  <p>
                    They also protect your investment. If you ever need to file an
                    insurance claim, most companies will ask whether the work was
                    permitted. Without proper documentation, you risk having your
                    claim denied when you need it most.
                  </p>
                  <p>
                    When your roof is properly permitted, you get more than
                    compliance. You get documentation that the work was done right,
                    meets every code requirement, and will stand up to Michigan
                    weather for years to come. That's the kind of protection that
                    matters when the next storm hits.
                  </p>
                    </>
                  )}
                </div>
              </div>

              <div className="rounded-xl overflow-hidden bg-neutral-200 min-h-[260px] md:min-h-[360px] lg:min-h-[460px]">
                {permitImg ? (
                  <GatsbyImage
                    image={permitImg}
                    alt="Roofing permits in Jackson, MI"
                    className="w-full h-full"
                  />
                ) : null}
              </div>
            </div>
            ) : null}
          </div>
        </section>
      );
    }}
  />
);

export default JacksonEmergencyAndCosts;
